import { ReactElement, useEffect, useState } from 'react';
import { useUIDSeed } from 'react-uid';
import { ErrorMessage, FeatureFlag } from 'config';

import useUpcomingMenus from 'hooks/useUpcomingMenus';
import useNotifications from 'hooks/useNotifications';
import { UpcomingMenuProvider } from 'contexts/UpcomingMenu';

import Notifications, { NotificationType } from 'models/Notifications';
import UpcomingMenu from 'models/UpcomingMenu';
import FetchState from 'models/FetchState';

import { Component as CookingIcon } from 'icons/icon-mktg--cook.svg';

import ErrorModal from 'components/shared/modals/ErrorModal';
import UpcomingMenuSkeleton from 'components/shared/skeletons/MenuSkeleton';
import ErrorPage from 'components/shared/ErrorPage';
import useCurrentUser from 'hooks/useCurrentUser';
import MobileAppPromoModal from './modals/MobileAppPromoModal';

import OrderConfirmationModal from './modals/OrderConfirmationModal';
import UpcomingMenuComponent from './UpcomingMenu';
import Header from './Header';
import { weekAndMonthDay } from '../../utils/dateTimes';

const UPDATED_MENU_ID = /cart_updated_id=(\d+)/;
const ERROR_MESSAGE = /error=([^?#]+)/;
const MENU_LIMIT = 4;

function findUpdatedMenu(upcomingMenus): UpcomingMenu | null {
  const matchUpdatedMenu = window.location.search.match(UPDATED_MENU_ID);
  let updatedMenu = null;

  if (!matchUpdatedMenu) return updatedMenu;

  const upcomingMenuId = parseInt(matchUpdatedMenu[1], 10);
  updatedMenu = upcomingMenus.find((menu) => menu.menu_id === upcomingMenuId);

  return updatedMenu;
}

function findError(): string | null {
  const error = window.location.search.match(ERROR_MESSAGE);
  return error && error[1] && ErrorMessage[error[1].toUpperCase()];
}

function UpcomingMenus(): ReactElement {
  const { upcomingMenus, fetchState } = useUpcomingMenus();
  const updatedMenu = findUpdatedMenu(upcomingMenus);
  const { notifications, dispatch } = useNotifications();
  const error = findError();
  const seed = useUIDSeed();
  const { currentUser } = useCurrentUser();

  // limit amount of menus displayed if ff is on
  const menuDisplayLimit = currentUser
    .hasFeatureEnabled(FeatureFlag.MENUS_DISPLAY_LIMIT) ? MENU_LIMIT : Infinity;

  const [isLtoMenuSkipped, setIsLtoMenuSkipped] = useState(false);

  const {
    hasTutorialTooltip,
    hasFavoritesAnnouncement,
  } = notifications;

  // @ts-expect-error TODO: fix null safety
  const nextSkipDeadline = weekAndMonthDay(upcomingMenus?.find((menu) => (
    new Date(menu.skip_deadline) > new Date()
  ))?.skip_deadline);

  const firstScheduledMenuIndex = upcomingMenus.findIndex((item:UpcomingMenu) => (
    item.arriving_status === 'scheduled'
  ));
  const firstSkippedMenuIndex = upcomingMenus.findIndex((item) => (
    !item.planned_or_confirmed
  ));

  useEffect(() => {
    const list = upcomingMenus.reduce((acc, menu) => {
      if (menu.menu_status_message) {
        acc.push({
          // @ts-expect-error TODO: fix null safety
          id: menu.menu_id,
          // @ts-expect-error TODO: fix null safety
          notification_type: NotificationType.MENU_STATUS_MESSAGE,
          // @ts-expect-error TODO: fix null safety
          message: menu.menu_status_message,
        });
      }
      return acc;
    }, []);

    dispatch({
      type: 'addNotifications',
      notifications: new Notifications(list),
    });
  }, [upcomingMenus, dispatch]);

  return (
    <div className="col-lg-10">
      <div className="c-upcoming-menu">
        <Header nextSkipDeadline={nextSkipDeadline} isLtoMenuSkipped={isLtoMenuSkipped} />

        {fetchState === FetchState.ERROR && (
          <div className="c-upcoming-menu-error-page">
            <ErrorPage />
          </div>
        )}

        {fetchState === FetchState.SUCCESS
        && upcomingMenus.slice(0, menuDisplayLimit).map((item, index) => (
          <UpcomingMenuProvider value={item} key={item.menu_id}>
            <UpcomingMenuComponent
              showTutorialTooltip={
                hasTutorialTooltip
                && index === firstScheduledMenuIndex
              }
              showFavoritesAnnouncement={
                hasFavoritesAnnouncement
                && index === firstScheduledMenuIndex
              }
              showAutoSkipTooltip={
                hasTutorialTooltip
                && currentUser.subscription?.delivery_frequency === 'one_off'
                && index === firstSkippedMenuIndex
              }
              setIsLtoMenuSkipped={setIsLtoMenuSkipped}
            />
          </UpcomingMenuProvider>
        ))}

        {(fetchState === FetchState.IN_PROGRESS
        && !upcomingMenus.length)
        && [...Array(3)].map((el, i) => (
          <UpcomingMenuSkeleton key={seed(i)} />
        ))}

        {fetchState !== FetchState.ERROR && (
          <div className="c-orders-still-cooking">
            <CookingIcon />
            <p className="u-text--md u-text--lg-lg">
              We’re still cooking!
              <br className="visible-xs" />
              Check back soon.
            </p>
          </div>
        )}

        {notifications.has('mobile_app_promo') && <MobileAppPromoModal />}
        {updatedMenu && <OrderConfirmationModal upcomingMenu={updatedMenu} />}
        {error && <ErrorModal message={error} />}
      </div>
    </div>
  );
}

export default UpcomingMenus;
